import { NavLink } from "react-router-dom";
import Icon from "./Icon";

export interface RailItem {
  to: string;
  label: string;
  icon: string;
  badge?: number | null;
  badgeTone?: "crit" | "warn" | "ok";
  roles?: string[];
  section?: string;
}

interface LeftRailProps {
  items: RailItem[];
  currentRole: string;
  onSignOut: () => void;
  connected?: boolean;
  /** Epoch ms of the newest GPS fix in the fleet, or null if none. */
  freshestFixAt?: number | null;
  /** Non-null when a request the shell depends on is failing. */
  dataError?: string | null;
  /** True until the first fleet snapshot has arrived. "No live GPS" is a
   *  finding about the fleet; before any positions have loaded there is no
   *  finding yet — on a fresh load, while an expired token was refreshed,
   *  this card read "No live GPS · DEGRADED" over a fleet that was fine. */
  connecting?: boolean;
}

/** Beyond this, the position pipeline is not "streaming" in any useful sense
 *  — it is the operator's cue that data has stopped arriving even though the
 *  socket is still open. */
const GPS_STREAM_STALE_MS = 5 * 60 * 1000;

function agoLabel(ms: number): string {
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function LeftRail({
  items,
  currentRole,
  onSignOut,
  connected = true,
  freshestFixAt = null,
  dataError = null,
  connecting = false,
}: LeftRailProps) {
  const visible = items.filter((i) => !i.roles || i.roles.includes(currentRole));

  // Group items by section
  const sections: { title: string; items: RailItem[] }[] = [];
  const sectionMap = new Map<string, RailItem[]>();
  for (const item of visible) {
    const key = item.section || "";
    if (!sectionMap.has(key)) {
      sectionMap.set(key, []);
      sections.push({ title: key, items: sectionMap.get(key)! });
    }
    sectionMap.get(key)!.push(item);
  }

  return (
    <aside className="occ-sidebar">
      {/* Header */}
      <div className="sb-header">
        <div className="sb-logo-wrap">
          <img src="/tap-logo-compact.png" alt="TAP" className="sb-logo" />
        </div>
        <div className="sb-org">
          <span className="sb-org-name">Fleet OCC</span>
          <span className="sb-org-sub">TRADE &amp; PROJECTS</span>
        </div>
      </div>

      {/* Nav sections */}
      <nav className="sb-nav">
        {sections.map((sec) => (
          <div className="sb-section" key={sec.title || "__ungrouped"}>
            {sec.title && <div className="sb-section-title">{sec.title}</div>}
            {sec.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => "sb-link" + (isActive ? " active" : "")}
              >
                <Icon name={it.icon} />
                <span className="sb-link-label">{it.label}</span>
                {it.badge != null && it.badge > 0 && (
                  <span className={"sb-badge" + (it.badgeTone ? " tone-" + it.badgeTone : "")}>
                    {it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Bottom area */}
      <div className="sb-bottom">
        {(() => {
          // Three distinct conditions, reported distinctly. Previously this
          // card asserted "GPS streaming" from transport health alone, so a
          // position pipeline that had been dead for weeks still read green.
          const fixAge = freshestFixAt ? Math.max(0, Date.now() - freshestFixAt) : null;
          const gpsStale = fixAge == null || fixAge > GPS_STREAM_STALE_MS;
          const pending = connected && connecting;
          const degraded = !pending && (!connected || !!dataError || gpsStale);
          const label = !connected
            ? "Connection lost"
            : connecting
              ? "Connecting…"
              : dataError
                ? "Degraded"
                : gpsStale
                  ? "No live GPS"
                  : "All systems live";
          const detail = !connected
            ? "Attempting to reconnect…"
            : connecting
              ? "Waiting for the first fleet snapshot"
              : dataError
                ? "API reachable · some data unavailable"
                : fixAge == null
                  ? "API connected · no GPS fixes received"
                  : gpsStale
                    ? `API connected · newest GPS fix ${agoLabel(fixAge)} old`
                    : "API connected · GPS streaming";
          return (
            <div className={"sb-status-card" + (pending ? " is-pending" : degraded ? " is-offline" : "")}>
              <div className="sb-status-header">
                <span className={"sb-status-dot" + (pending ? " pending" : degraded ? " offline" : "")} />
                <span className="sb-status-label">{label}</span>
              </div>
              <span className="sb-status-detail">{detail}</span>
              <span className={"sb-status-badge" + (pending ? " pending" : degraded ? " crit" : " ok")}>
                {pending ? "CONNECTING" : connected && !dataError && gpsStale ? "DEGRADED" : degraded ? "DISRUPTED" : "OPERATIONAL"}
              </span>
            </div>
          );
        })()}

        <button className="sb-signout" type="button" onClick={onSignOut}>
          <Icon name="signout" size={16} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
